import React, { useMemo } from "react";

import TemplateFormScreen from "./TemplateFormScreen";
import useGetTemplate from "../hooks/useGetTemplate";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

import { useQueryParams, BooleanParam, StringParam } from "use-query-params";
import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Button,
  Link,
  EmptyState
} from "../components/wombat-ui";
import { EditIcon } from "../components/icons";

const EditTemplateScreen = ({ match }) => {
  const t = useTranslations();

  const id = match?.params?.id;
  const { data, loading } = useGetTemplate({ variables: { id } });

  const [{ copied, reportId, madId }] = useQueryParams({
    copied: BooleanParam,
    reportId: StringParam,
    madId: StringParam,
  });

  // if the template is a default template
  // we set up the base template as a brand new
  // template that will be linked to the current user company
  const mustCopy = copied || data?.template?.default;

  const template = useMemo(() => {
    const template = data?.template;

    if (!template) {
      return null;
    }

    // NOTE: The direct string comparison should be replaced by a proper QS parser when needed
    if (mustCopy) {
      return {
        ...template,
        id: null,
        default: false,
        title: t("templates.copyOf", { originalTitle: template.title }),
      };
    } else {
      return template;
    }
  }, [data?.template, mustCopy]);

  const handleDuplicate = useMemo(() => `/templates/${id}/copy?copied=1`, [id]);

  const {
    isCapableOfInsertingTemplates,
  } = useViewer();

  const canInsert = useMemo(() => {
    if (mustCopy) {
      return (isCapableOfInsertingTemplates);
    }
    return (data?.template?.canInsert);
  }, [isCapableOfInsertingTemplates, mustCopy, data?.template]);

  const returnUrl = useMemo(() => {
    if (reportId) {
      return (`/reports/${reportId}`);
    }
    if (madId) {
      return (`/mads/${madId}`);
    }
    return ("/templates");
  })

  return (
    <ScreenContainer
      loading={loading}
      error={
        !canInsert ? (
          <EmptyState
            title={t("network.unauthorized")}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }  
    >
      {template && !loading && (
        <>
          <ScreenHeader
            title={template.title}
            icon={EditIcon}
            actions={[
              !mustCopy && !madId && isCapableOfInsertingTemplates && (
                <Link
                  key="duplicate"
                  variant="button-subtle"
                  to={handleDuplicate}
                >
                  {t("forms.actions.duplicate")}
                </Link>
              ),
              <Button key="update" type="submit" form="templateForm">
                {t("forms.actions.send")}
              </Button>,
            ]}
          />
          <Container>
            <TemplateFormScreen
              template={template}
              returnUrl={returnUrl}
              shouldSelectReferent={madId || reportId}
            />
          </Container>
        </>
      )}
    </ScreenContainer>
  );
};

export default EditTemplateScreen;
